// =============================================================================
// TitleView.ts — Tela de abertura: vídeo do "Terceiro Turno" em loop de fundo
// e a cabine de identificação por cima (login social). As opções são dinâmicas:
// deslogado vê ENTRAR COM GOOGLE; logado vê CONTINUAR COMO FULANO / SAIR.
// Setas/WS navegam, ENTER confirma, mouse também vale.
// =============================================================================
import { audio } from '../AudioEngine';
import { $ } from './dom';

export interface TitleOption {
  label: string;
  icon?: 'google';       // ícone pixel art à esquerda do rótulo
  action: () => void;
}

// "G" do Google em pixel art (nada de emoji nem botão oficial glossy):
// as quatro cores da firma em rects de grade 10x10, borda dura
const GOOGLE_G_SVG = `
  <svg class="pix-icon title-opcao-icon" width="22" height="22" viewBox="0 0 10 10"
    shape-rendering="crispEdges" aria-hidden="true">
    <g fill="#ea4335"><rect x="2" y="0" width="6" height="1"/><rect x="1" y="1" width="1" height="1"/><rect x="8" y="1" width="1" height="1"/></g>
    <g fill="#fbbc05"><rect x="0" y="2" width="1" height="6"/><rect x="1" y="8" width="1" height="1"/></g>
    <g fill="#34a853"><rect x="2" y="9" width="6" height="1"/><rect x="8" y="8" width="1" height="1"/></g>
    <g fill="#4285f4"><rect x="5" y="4" width="5" height="2"/><rect x="9" y="6" width="1" height="2"/></g>
  </svg>`;

export class TitleView {
  private readonly video = $('title-video') as HTMLVideoElement;
  private readonly menu = $('title-menu');
  private opcoes: HTMLButtonElement[] = [];
  private acoes: Array<() => void> = [];
  private sel = 0;
  // Quem pediu menos movimento ganha o poster parado (o vídeo é só decoração)
  private readonly semAnimacao = matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Tela em pé (celular) toca o corte portrait do vídeo em vez do widescreen
  private readonly emPe = matchMedia('(max-aspect-ratio: 1/1)');

  constructor() {
    document.addEventListener('keydown', (e) => this.onKey(e));

    if (this.semAnimacao) {
      this.video.autoplay = false;
      this.video.pause();
    }
    this.applyVideoSource();
    this.emPe.addEventListener('change', () => this.applyVideoSource()); // girou o celular
  }

  /** Troca o menu inteiro (chega o /api/me, loga, desloga...). */
  setOptions(options: TitleOption[]): void {
    this.menu.innerHTML = '';
    this.acoes = options.map((o) => o.action);
    this.opcoes = options.map((o, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'title-opcao';
      if (o.icon === 'google') btn.insertAdjacentHTML('beforeend', GOOGLE_G_SVG);
      btn.appendChild(document.createTextNode(o.label));
      btn.onclick = () => { this.select(i, false); this.confirm(); };
      btn.onmouseenter = () => this.select(i, false);
      btn.onfocus = () => this.select(i, false);
      this.menu.appendChild(btn);
      return btn;
    });
    this.sel = 0;
    this.opcoes[0]?.classList.add('sel');
  }

  /** Linha de status embaixo do menu (erro de login, quem está logado). */
  setStatus(text: string, isError = false): void {
    const el = $('title-status');
    el.textContent = text;
    el.classList.toggle('title-status-erro', isError);
  }

  // Escolhe o corte do vídeo pela orientação. Trocar src reinicia o loop,
  // mas isso só acontece ao girar o aparelho — aceitável pra uma decoração.
  private applyVideoSource(): void {
    const portrait = this.emPe.matches;
    const src = portrait ? '/assets/title-mobile.mp4' : '/assets/title.mp4';
    if (this.video.src.endsWith(src)) return;
    this.video.poster = portrait ? '/assets/title-poster-mobile.jpg' : '/assets/title-poster.jpg';
    this.video.src = src; // sobrepõe o <source> de fallback e já dispara load()
    // No body: o vídeo mora no #video-bg (fundo de todas as telas de
    // navegação desde a issue #67), fora do #screen-title
    document.body.classList.toggle('portrait-video', portrait);
    if (!this.semAnimacao) this.video.play().catch(() => { /* autoplay barrado: fica no poster */ });
  }

  private isActive(): boolean {
    return $('screen-title').classList.contains('active');
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.isActive() || !this.opcoes.length) return;
    const k = e.key.toLowerCase();
    if (k === 'arrowdown' || k === 's') this.select(this.sel + 1);
    else if (k === 'arrowup' || k === 'w') this.select(this.sel - 1);
    else if (k === 'enter' || k === ' ') this.confirm();
    else return;
    e.preventDefault();
  }

  private select(i: number, beep = true): void {
    const novo = (i + this.opcoes.length) % this.opcoes.length;
    if (novo === this.sel && this.opcoes[this.sel].classList.contains('sel')) return;
    this.sel = novo;
    this.opcoes.forEach((btn, j) => btn.classList.toggle('sel', j === this.sel));
    if (beep) audio.sfx.urnaKey();
  }

  private confirm(): void {
    if (!this.acoes.length) return;
    audio.sfx.urnaKey();
    this.acoes[this.sel]();
  }
}
